import React, { useState, useEffect } from 'react';
import {
  FaGlobeAmericas,
  FaBriefcase,
  FaHome,
  FaUsers,
  FaCheckCircle,
  FaShieldAlt,
  FaClock,
  FaHandshake,
  FaAward,
  FaPassport,
  FaPlane,
  FaQuoteLeft,
  FaChevronLeft,
  FaChevronRight,
  FaCalculator
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'motion/react';
import '../Styles/LibaMigration.css';
import Reveal, { RevealGroup, RevealItem } from './Reveal';
import MagneticButton from './MagneticButton';

const testimonials = [
  {
    name: "Vikram Rathore",
    destination: "Toronto, Canada",
    text: "Liba Migration handled my Express Entry profile flawlessly. From WES evaluation to receiving my COPR, the team was transparent, fast, and highly supportive.",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&crop=faces&q=80",
    service: "Canada PR (Express Entry)"
  },
  {
    name: "Sneha Nair",
    destination: "Vancouver, Canada",
    text: "Getting my Canada Work Permit was a stressful process until I consulted Liba Migration. They helped my employer with the LMIA approval and handled my work visa.",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=faces&q=80",
    service: "LMIA Work Permit"
  },
  {
    name: "Amanpreet Singh",
    destination: "Calgary, Canada",
    text: "I completed my studies in Canada and wanted to transition to PR. Liba Migration outlined the perfect Study-to-PR pathway and managed my CEC application successfully.",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&crop=faces&q=80",
    service: "Study to PR Pathway"
  }
];

const steps = [
  { num: 1, title: "Free Assessment", desc: "Evaluate your eligibility and profile strength across potential pathways." },
  { num: 2, title: "Strategy Planning", desc: "Customized immigration pathway mapped out based on your profile and goals." },
  { num: 3, title: "Documentation", desc: "Complete document preparation, credential assessment, and verification." },
  { num: 4, title: "Application Filing", desc: "Submit application streams with 100% legal compliance and accuracy." },
  { num: 5, title: "Follow-up & Updates", desc: "Regular tracking of your visa progress and prompt communication." },
  { num: 6, title: "Success & Settlement", desc: "COPR/Visa receipt accompanied by post-landing settlement support." }
];

export default function LibaMigration() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Testimonials Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="liba-page">
      <Helmet>
        <title>Liba Migration Services | Canada PR & Work Permit Experts</title>
        <meta name="description" content="Partner with Liba Migration Services, sister concern of VE Abroad, for specialized Canada PR, work permits, LMIA processing, and business visas." />
        <link rel="canonical" href="https://veabroad.com/LibaMigration" />
        <meta property="og:title" content="Liba Migration Services | Canada PR & Work Permit Experts" />
        <meta property="og:description" content="Partner with Liba Migration Services, sister concern of VE Abroad, for specialized Canada PR, work permits, LMIA processing, and business visas." />
        <meta property="og:url" content="https://veabroad.com/LibaMigration" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Liba Migration Services | Canada PR & Work Permit Experts" />
        <meta name="twitter:description" content="Partner with Liba Migration Services, sister concern of VE Abroad, for specialized Canada PR, work permits, LMIA processing, and business visas." />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Liba Migration Services",
            "image": "https://veabroad.com/libaLogo.png",
            "telephone": "+91 8850285886",
            "url": "https://veabroad.com/LibaMigration",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Amar Jyoti CHS, Andheri West",
              "addressLocality": "Mumbai",
              "addressRegion": "Maharashtra",
              "postalCode": "400053",
              "addressCountry": "IN"
            },
            "parentOrganization": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            }
          })}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="liba-hero position-relative overflow-hidden">
        {/* Animated Glow Blobs */}
        <div className="liba-hero-blob liba-blob-1" />
        <div className="liba-hero-blob liba-blob-2" />
        <div className="liba-hero-grid" />
        
        <div className="container position-relative z-2 py-5">
          <div className="row text-center justify-content-center">
            <div className="col-lg-10">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="liba-badge va-float mb-3 d-inline-block px-3 py-1 rounded-pill">
                  🌍 Sister Concern of VE-Scholars
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="liba-hero-title"
              >
                Liba <span className="va-shine-text va-shine-text-on-dark">Migration</span> Services
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="liba-hero-subtitle"
              >
                Your Trusted Partner for Canada PR, Work Permits & Global Immigration Solutions
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="liba-hero-stats d-flex justify-content-center gap-4 flex-wrap"
              >
                <div className="stat-box va-hover-lift">
                  <h3 className="va-pop">8+</h3>
                  <p>Years Experience</p>
                </div>
                <div className="stat-box va-hover-lift">
                  <h3 className="va-pop">500+</h3>
                  <p>Families Settled</p>
                </div>
                <div className="stat-box va-hover-lift">
                  <h3 className="va-pop">98%</h3>
                  <p>Success Rate</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="section-liba py-5 bg-white">
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="section-title">About Liba <span className="va-shine-text">Migration</span> Services</h2>
                <p className="section-text">
                  Liba Migration Services is the specialized migration arm of the VE-Scholars Group, dedicated exclusively to providing comprehensive immigration solutions for individuals, families, and professionals seeking global opportunities.
                </p>
                <p className="section-text">
                  With over 8 years of proven expertise in international migration, we have successfully helped 500+ families establish new lives in Canada and other leading destinations. Our team of certified immigration consultants ensures full compliance, transparency, and personalized strategies for every client.
                </p>
                <div className="liba-highlights mt-4">
                  <div className="highlight-box va-hover-lift">
                    <FaShieldAlt className="highlight-icon" />
                    <div>
                      <h4>Full Compliance</h4>
                      <p>100% legal, regulated, and transparent process</p>
                    </div>
                  </div>
                  <div className="highlight-box va-hover-lift">
                    <FaHandshake className="highlight-icon" />
                    <div>
                      <h4>Personalized Guidance</h4>
                      <p>Custom-tailored visa strategy aligned with your credentials</p>
                    </div>
                  </div>
                  <div className="highlight-box va-hover-lift">
                    <FaCalculator className="highlight-icon" />
                    <div>
                      <h4>Transparent Fee Structure</h4>
                      <p>No hidden charges — every service cost is explained upfront</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="position-relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&h=500&fit=crop&q=70" 
                  alt="Liba Migration" 
                  className="img-fluid rounded-4 shadow-lg w-100" 
                  loading="lazy" 
                />
                <div className="image-accent-glow" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="section-liba py-5 light-bg">
        <div className="container py-4">
          <Reveal as="h2" className="section-title text-center mb-5">Our Specialized Services</Reveal>
          <div className="row g-4">
            {[
              {
                icon: <FaHome />,
                title: "Canada Permanent Residency",
                desc: "Express Entry, PNP, Family Sponsorship, and all PR pathways to settle permanently in Canada.",
                points: ["CRS Score Optimization", "Provincial Nominee Programs", "Document Preparation", "Post-Landing Support"]
              },
              {
                icon: <FaBriefcase />,
                title: "Work Permits",
                desc: "LMIA-based work permits, open work permits, and employer-specific work authorization.",
                points: ["LMIA Processing", "Job Search Support", "Work Permit Extensions", "Spouse Work Permits"]
              },
              {
                icon: <FaPassport />,
                title: "Business Immigration",
                desc: "Entrepreneur programs, investor visas, and startup visa pathways for business professionals.",
                points: ["Business Plan Development", "Investment Guidance", "Startup Visa Program", "Self-Employed Programs"]
              },
              {
                icon: <FaUsers />,
                title: "Family Sponsorship",
                desc: "Reunite with your loved ones through spouse, parent, and dependent sponsorship programs.",
                points: ["Spouse Sponsorship", "Parent & Grandparent Program", "Dependent Children", "Super Visa Applications"]
              },
              {
                icon: <FaPlane />,
                title: "Study to PR Pathway",
                desc: "Strategic planning from study permit to permanent residency for international students.",
                points: ["Post-Graduation Work Permit", "Canadian Experience Class", "Career Pathway Planning", "PR Application Strategy"]
              },
              {
                icon: <FaGlobeAmericas />,
                title: "Global Immigration",
                desc: "Migration services for Australia, UK, USA, and other leading immigration destinations.",
                points: ["Australia Skilled Migration", "UK Skilled Worker Visa", "USA EB-5 & H1B", "Europe Work Permits"]
              }
            ].map((s, idx) => (
              <div className="col-lg-4 col-md-6" key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  whileHover={{ y: -8 }}
                  className="service-card shadow-sm h-100"
                >
                  <div className="service-icon mb-4">
                    {s.icon}
                  </div>
                  <h3 className="mb-3">{s.title}</h3>
                  <p className="mb-4">{s.desc}</p>
                  <ul className="service-features">
                    {s.points.map((p, pIdx) => (
                      <li key={pIdx}><FaCheckCircle /> {p}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* WHY CHOOSE LIBA */}
      <section className="section-liba py-5 light-bg">
        <div className="container py-4">
          <Reveal as="h2" className="section-title text-center mb-5">Why Choose Liba Migration Services?</Reveal>
          <div className="row g-4 why-cards-mobile-grid">
            {[
              { icon: <FaAward />, title: "8+ Years Expertise", desc: "Proven track record in Canadian and global immigration pathways." },
              { icon: <FaShieldAlt />, title: "100% Compliance", desc: "Fully transparent processes aligned with regulated legal updates." },
              { icon: <FaUsers />, title: "500+ Families Settled", desc: "Successfully guided families establishing roots abroad." },
              { icon: <FaClock />, title: "End-to-End Support", desc: "Dedicated team for credential checks, filing, and pre/post landing." }
            ].map((w, idx) => (
              <div className="col-lg-3 col-md-6" key={idx}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.93 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="why-card shadow-sm h-100 va-hover-lift"
                >
                  <div className="why-icon mb-3">
                    {w.icon}
                  </div>
                  <h4>{w.title}</h4>
                  <p>{w.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MIGRATION PROCESS TIMELINE */}
      <section className="section-liba py-5 bg-white">
        <div className="container py-4">
          <Reveal as="h2" className="section-title text-center mb-5">Our Migration Process</Reveal>
          <div className="liba-timeline position-relative mt-5">
            {/* Timeline Line */}
            <div className="liba-timeline-line" />
            
            <div className="row g-4">
              {steps.map((s, idx) => (
                <div className="col-lg-4 col-md-6" key={idx}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: idx * 0.08 }}
                    className="liba-timeline-item p-4 rounded-4 shadow-sm border h-100 va-hover-lift"
                  >
                    <div className="liba-timeline-header d-flex align-items-center gap-3 mb-3">
                      <div className="liba-timeline-number flex-shrink-0">
                        {s.num}
                      </div>
                      <h4 className="fw-bold text-teal m-0">{s.title}</h4>
                    </div>
                    <p className="text-muted m-0 small" style={{ lineHeight: '1.5' }}>{s.desc}</p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT TESTIMONIALS SLIDER */}
      <section className="section-liba py-5 light-bg overflow-hidden position-relative">
        <div className="container py-4 position-relative z-2">
          <Reveal as="h2" className="section-title text-center mb-5">Client Success Stories</Reveal>
          
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="liba-testimonial-slider-wrap p-4 p-md-5 rounded-4 shadow-lg bg-white border position-relative">
                <FaQuoteLeft className="testimonial-quote-icon position-absolute opacity-10" />
                
                <div className="testimonial-slider-container">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.4 }}
                      className="testimonial-slide text-center"
                    >
                      <p className="lead testimonial-text mb-4 text-dark italic" style={{ fontSize: '1.15rem', fontStyle: 'italic', lineHeight: '1.7' }}>
                        "{testimonials[currentSlide].text}"
                      </p>
                      
                      <div className="testimonial-author d-flex align-items-center justify-content-center gap-3">
                        <img 
                          src={testimonials[currentSlide].avatar} 
                          alt={testimonials[currentSlide].name} 
                          className="rounded-circle border border-2 border-teal"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }} 
                        />
                        <div className="text-start">
                          <h5 className="fw-bold text-teal m-0" style={{ fontSize: '1rem' }}>{testimonials[currentSlide].name}</h5>
                          <span className="text-muted small d-block">{testimonials[currentSlide].destination}</span>
                          <span className="badge bg-teal-subtle text-teal mt-1 fw-bold" style={{ fontSize: '0.75rem', background: '#e8f4f3', color: '#1d4e50' }}>
                            {testimonials[currentSlide].service}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
                
                {/* Chevrons */}
                <button 
                  onClick={handlePrevSlide} 
                  className="testimonial-nav-btn prev position-absolute d-flex align-items-center justify-content-center border shadow-sm rounded-circle bg-white"
                  aria-label="Previous Success Story"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  onClick={handleNextSlide} 
                  className="testimonial-nav-btn next position-absolute d-flex align-items-center justify-content-center border shadow-sm rounded-circle bg-white"
                  aria-label="Next Success Story"
                >
                  <FaChevronRight />
                </button>
                
                {/* Slide dots */}
                <div className="testimonial-dots d-flex justify-content-center gap-2 mt-4">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      className={`testimonial-dot border-0 rounded-circle ${currentSlide === i ? "active" : ""}`}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="liba-cta position-relative py-5 overflow-hidden">
        {/* Glow Effects */}
        <div className="liba-hero-blob liba-blob-1" style={{ top: '-10%', left: '30%', width: '300px', height: '300px' }} />
        <div className="liba-hero-grid" />
        
        <div className="container position-relative z-2 py-4">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <Reveal as="h2" className="mb-3 text-white">Ready to Start Your Immigration Journey?</Reveal>
              <Reveal as="p" delay={0.1} className="lead text-white-50 mb-5">Get expert guidance from Liba Migration Services and make your global dreams a reality</Reveal>
              <RevealGroup as="div" className="cta-buttons d-flex justify-content-center gap-3 flex-wrap" stagger={0.12}>
                <RevealItem as="div" direction="zoom">
                  <MagneticButton as="span">
                    <Link to="/consultationForm" className="btn-primary-liba va-shimmer-btn py-3 px-4 rounded-3 text-decoration-none fw-bold shadow-md d-inline-block">
                      Book Free Consultation
                    </Link>
                  </MagneticButton>
                </RevealItem>
                <RevealItem as="div" direction="zoom">
                  <Link to="/migrate" className="btn-outline-liba py-3 px-4 rounded-3 text-decoration-none fw-bold d-inline-block">
                    Explore Migration Options
                  </Link>
                </RevealItem>
              </RevealGroup>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
